import { useCallback, useEffect, useMemo, useState } from "react";
import type { Indicator } from "../types";
import { formatDataUrl } from "../utils";
import { InfoTooltip } from "./common";
import { ChartPanel } from "./ChartPanel";

function IndicatorSection({ indicator, index }: { indicator: Indicator; index: number }) {
  const defaultSelections = useMemo(() => {
    const entries = indicator.charts.flatMap((chart) => {
      if (!chart.selection) {
        return [];
      }

      return [[chart.selection.key, chart.selection.defaultValue] as const];
    });

    return Object.fromEntries(entries);
  }, [indicator.charts]);
  const [selections, setSelections] = useState<Record<string, string>>(defaultSelections);

  useEffect(() => {
    setSelections(defaultSelections);
  }, [defaultSelections]);

  const handleSelectionChange = useCallback((key: string, value: string) => {
    setSelections((current) => ({ ...current, [key]: value }));
  }, []);

  return (
    <article id={indicator.id} className="indicator-section">
      <div className="indicator-header">
        <div className="indicator-header__title-block">
          <span className="indicator-header__index">{String(index + 1).padStart(2, "0")}</span>
          <div className="indicator-header__copy">
            <p className="indicator-header__category">{indicator.category}</p>
            <div className="title-with-info">
              <h2>{indicator.title}</h2>
              <InfoTooltip label={`${indicator.title}の説明`} text={indicator.intent} />
            </div>
          </div>
        </div>
        <div className="indicator-actions">
          <span className="status-pill">{indicator.latest.status}</span>
          <SourceDetails indicator={indicator} />
          <a className="ghostbtn" href={formatDataUrl(indicator.csvPath)} download>
            CSV
          </a>
        </div>
      </div>

      <div className="chart-grid">
        {indicator.charts.map((chart) => (
          <ChartPanel
            key={chart.id}
            chart={chart}
            selectedValue={chart.selection ? selections[chart.selection.key] ?? chart.selection.defaultValue : undefined}
            onSelectionChange={handleSelectionChange}
          />
        ))}
      </div>
    </article>
  );
}

function SourceDetails({ indicator }: { indicator: Indicator }) {
  return (
    <details className="source-details meta">
      <summary>データ元</summary>
      <dl className="source-grid" aria-label={`${indicator.title}のメタ情報`}>
        <div>
          <dt>出典</dt>
          <dd>
            <a href={indicator.metadata.sourceUrl} target="_blank" rel="noreferrer">
              {indicator.metadata.sourceName}
            </a>
          </dd>
        </div>
        <div>
          <dt>取得日</dt>
          <dd>{indicator.metadata.retrievedAt}</dd>
        </div>
        <div>
          <dt>粒度</dt>
          <dd>{indicator.metadata.granularity}</dd>
        </div>
        <div>
          <dt>更新</dt>
          <dd>{indicator.metadata.updateFrequency}</dd>
        </div>
        <div>
          <dt>加工</dt>
          <dd>{indicator.metadata.processing}</dd>
        </div>
        <div>
          <dt>利用条件</dt>
          <dd>{indicator.metadata.license}</dd>
        </div>
      </dl>
    </details>
  );
}

export { IndicatorSection, SourceDetails };
